import { AsyncPipe, NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, HostBinding, Input, ViewChild, inject, output, type OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router, type Params } from '@angular/router';
import { UiAutocompleteInputComponent, type AutocompleteOption } from '@big-direkt/ui/input';
import { IconBigMediumSuche, IconComponent } from '@big-direkt/utils/icons';
import { translateObjectSignal } from '@jsverse/transloco';
import { Subject, merge, of, type Observable } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, filter, map, switchMap } from 'rxjs/operators';
import { v4 as uuidv4 } from 'uuid';
import { SearchTrackingService } from '../../services/search-tracking.service';
import { SearchService } from '../../services/search.service';

@Component({
    selector: 'big-search-form',
    templateUrl: './search-form.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [AsyncPipe, FormsModule, IconComponent, NgClass, ReactiveFormsModule, UiAutocompleteInputComponent],
})
export class SearchFormComponent implements OnInit {
    private readonly activatedRoute = inject(ActivatedRoute);
    private readonly destroyRef = inject(DestroyRef);
    private readonly resetSearchTerm$ = new Subject<void>();
    private readonly router = inject(Router);
    private readonly searchService = inject(SearchService);
    private readonly searchTrackingService = inject(SearchTrackingService);
    private readonly suggestDebounceTime = 300;

    public readonly iconSearch = IconBigMediumSuche;
    public readonly submitted = output();

    public readonly translate = translateObjectSignal('search');

    @HostBinding('class') @Input() public classList = 'block';
    @Input() public placeholder?: string;
    @Input() public position = 'bottom-left';
    @Input() public clearOnSubmit = true;
    @Input() public setTermFromQuery = false;
    @Input() public input = false;
    @Input({ required: true }) public location!: 'header' | 'search-box' | 'search-results' | 'side-nav';

    @ViewChild(UiAutocompleteInputComponent) public autocompleteInput?: UiAutocompleteInputComponent<string>;

    public id: string = uuidv4();
    public suggestions$?: Observable<AutocompleteOption<string>[]>;
    public inputStyle?: string;
    public autocompleteStyle: string | undefined;
    public searchForm = new FormGroup<{ term: FormControl<string | null> }>({
        term: new FormControl<string>(''),
    });

    public ngOnInit(): void {
        if (this.setTermFromQuery) {
            this.activatedRoute.queryParams.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params: Params): void => {
                // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
                this.searchForm.controls.term.setValue(params.q ?? '');
            });
        }
        this.handleSearchTermChanges(this.searchForm.controls.term.value, this.searchForm.controls.term.valueChanges, this.resetSearchTerm$.asObservable());

        this.setInputTypeStyles();
        this.setAutocompleteStyle();
    }

    public onSearchTermChanged(term: string | null | undefined): void {
        this.searchForm.controls.term.setValue(term ?? '');
    }

    public handleSearchTermChanges(text: string | null, text$: Observable<string | null>, resetSearchTerm$: Observable<void>): void {
        this.suggestions$ = merge(
            this.searchService.getSuggestions(text ?? '').pipe(
                catchError(this.onSuggestionsQueryError),
                map(suggestions => suggestions.map(sug => this.toOption(sug))),
            ),
            text$.pipe(
                distinctUntilChanged(),
                debounceTime(this.suggestDebounceTime),
                filter<string | null, string>((value): value is string => !!value || value === ''),
                switchMap(
                    (term: string): Observable<AutocompleteOption<string>[]> =>
                        this.searchService.getSuggestions(term).pipe(
                            catchError(this.onSuggestionsQueryError),
                            map(suggestions => suggestions.map(sug => this.toOption(sug))),
                        ),
                ),
            ),
            resetSearchTerm$.pipe(map(() => [])),
        );
    }

    public onOptionSelected(): void {
        this.submit('search suggestion');
    }

    public transformOptionValue(option: AutocompleteOption<string>): string {
        return option.label;
    }

    public submit(selectedOptionLabel?: string | null): void {
        this.autocompleteInput?.autocomplete?.closePanel();

        this.submitted.emit();

        const value = this.searchForm.controls.term.value ?? '';

        this.searchTrackingService.trackingInfoState = {
            q: value,
            src: this.router.url,
            cat: this.location === 'search-box' ? 'search-box' : 'search-bar',
            entryType: selectedOptionLabel === undefined ? 'manual_search_entry' : 'automatic_search_suggestion',
            isNewSearchSubmit: true,
        };

        if (this.clearOnSubmit) {
            this.searchForm.reset();
            this.resetSearchTerm$.next();
        }

        this.searchService.term.next(value);
        void this.router.navigate(['suche'], {
            queryParams: {
                q: value,
            },
            replaceUrl: true,
        });
    }

    private toOption(term: string): AutocompleteOption<string> {
        return { label: term, key: term, value: term };
    }

    private readonly onSuggestionsQueryError = (): Observable<string[]> => of([]);

    private setAutocompleteStyle(): void {
        const { location } = this;
        let styles: string[] = [];

        if (location === 'header' || location === 'side-nav') {
            styles = [
                'py-3!',
                'rounded-b-xl!',
                '[clip-path:inset(4px_-10px_-10px_-10px)]',
                'before:absolute',
                'before:bg-primary-300',
                'before:content[""]',
                'before:h-[5px]',
                'before:left-3',
                'before:right-4',
                'before:top-0',
            ];
        }

        if (location === 'side-nav') {
            styles.push('before:bottom-2');
        }

        if (location === 'search-box' || location === 'search-results') {
            styles = [
                'rounded-b-md!',
                '[clip-path:inset(4px_-10px_-10px_-10px)]',
                '-top-1',
                'absolute!',
                'py-0',
                'min-h-[1rem]',
                'before:absolute',
                'before:bg-gray-300',
                'before:h-[1px]',
                'before:left-4',
                'before:right-6',
                'before:top-1',
            ];
        }

        this.autocompleteStyle = styles.join(' ');
    }

    private setInputTypeStyles(): void {
        const { location } = this;

        const styles = [
            'not-placeholder-shown:border-r-gray-300!',
            'bg-transparent',
            'dark:bg-transparent!',
            'inset-ring-0',
            '[&.form-control:enabled:not(:read-only).is-open]:inset-ring-0',
            '[&.form-control:enabled:not(:read-only).is-invalid]:inset-ring-0',
            'focus:border-r-gray-300!',
            'form-control',
            'leading-4',
            'outline-hidden',
            'pl-4',
            'pr-10',
            'rounded-none',
            'w-full',
        ];

        if (location === 'header' || location === 'side-nav') {
            styles.push('py-0');
        }

        if (location === 'search-results' || location === 'search-box') {
            styles.push('py-0 my-2');
        }

        this.inputStyle = styles.join(' ');
    }
}
